import { SMART_GUIDE_SNAP_THRESHOLD } from './constants';
import { ActiveSmartGuide } from './types';

type HandleType =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'top'
  | 'right'
  | 'bottom'
  | 'left';

interface CalculateResizeSnapParams {
  newLeft: number;
  newTop: number;
  newWidth: number;
  newHeight: number;
  canvasWidth: number;
  canvasHeight: number;
  handleType: HandleType;
}

interface ResizeSnapResult {
  snappedLeft: number;
  snappedTop: number;
  snappedWidth: number;
  snappedHeight: number;
  activeGuides: ActiveSmartGuide[];
}

export function calculateResizeSnap({
  newLeft,
  newTop,
  newWidth,
  newHeight,
  canvasWidth,
  canvasHeight,
  handleType,
}: CalculateResizeSnapParams): ResizeSnapResult {
  const activeGuides: ActiveSmartGuide[] = [];
  let snappedLeft = newLeft;
  let snappedTop = newTop;
  let snappedWidth = newWidth;
  let snappedHeight = newHeight;

  const canvasCenterX = canvasWidth / 2;
  const canvasCenterY = canvasHeight / 2;

  const newRight = newLeft + newWidth;
  const newBottom = newTop + newHeight;

  const affectsLeft = handleType.includes('left');
  const affectsRight = handleType.includes('right') || handleType === 'right';
  const affectsTop = handleType.includes('top') || handleType === 'top';
  const affectsBottom =
    handleType.includes('bottom') || handleType === 'bottom';

  if (affectsLeft) {
    const distanceToLeftEdge = Math.abs(newLeft);
    const distanceToCenter = Math.abs(newLeft - canvasCenterX);

    if (distanceToLeftEdge <= SMART_GUIDE_SNAP_THRESHOLD) {
      const diff = newLeft;
      snappedLeft = 0;
      snappedWidth = newWidth + diff;
      activeGuides.push({ type: 'left-edge', position: 0 });
    } else if (distanceToCenter <= SMART_GUIDE_SNAP_THRESHOLD) {
      const diff = newLeft - canvasCenterX;
      snappedLeft = canvasCenterX;
      snappedWidth = newWidth + diff;
      activeGuides.push({ type: 'vertical-center', position: canvasCenterX });
    }
  }

  if (affectsRight) {
    const distanceToRightEdge = Math.abs(newRight - canvasWidth);
    const distanceToCenter = Math.abs(newRight - canvasCenterX);

    if (distanceToRightEdge <= SMART_GUIDE_SNAP_THRESHOLD) {
      snappedWidth = canvasWidth - snappedLeft;
      activeGuides.push({ type: 'right-edge', position: canvasWidth });
    } else if (distanceToCenter <= SMART_GUIDE_SNAP_THRESHOLD) {
      snappedWidth = canvasCenterX - snappedLeft;
      activeGuides.push({ type: 'vertical-center', position: canvasCenterX });
    }
  }

  if (affectsTop) {
    const distanceToTopEdge = Math.abs(newTop);
    const distanceToCenter = Math.abs(newTop - canvasCenterY);

    if (distanceToTopEdge <= SMART_GUIDE_SNAP_THRESHOLD) {
      const diff = newTop;
      snappedTop = 0;
      snappedHeight = newHeight + diff;
      activeGuides.push({ type: 'top-edge', position: 0 });
    } else if (distanceToCenter <= SMART_GUIDE_SNAP_THRESHOLD) {
      const diff = newTop - canvasCenterY;
      snappedTop = canvasCenterY;
      snappedHeight = newHeight + diff;
      activeGuides.push({ type: 'horizontal-center', position: canvasCenterY });
    }
  }

  if (affectsBottom) {
    const distanceToBottomEdge = Math.abs(newBottom - canvasHeight);
    const distanceToCenter = Math.abs(newBottom - canvasCenterY);

    if (distanceToBottomEdge <= SMART_GUIDE_SNAP_THRESHOLD) {
      snappedHeight = canvasHeight - snappedTop;
      activeGuides.push({ type: 'bottom-edge', position: canvasHeight });
    } else if (distanceToCenter <= SMART_GUIDE_SNAP_THRESHOLD) {
      snappedHeight = canvasCenterY - snappedTop;
      activeGuides.push({ type: 'horizontal-center', position: canvasCenterY });
    }
  }

  return {
    snappedLeft: Math.round(snappedLeft),
    snappedTop: Math.round(snappedTop),
    snappedWidth: Math.max(1, Math.round(snappedWidth)),
    snappedHeight: Math.max(1, Math.round(snappedHeight)),
    activeGuides,
  };
}
